import { useState, useCallback } from 'react';
import { AlignLeft, Minimize2, AlertCircle } from 'lucide-react';

interface JsonEditorProps {
  value: object;
  onChange: (value: object) => void;
  placeholder?: string;
  rows?: number;
  validate?: (obj: object) => string | null;
}

export default function JsonEditor({
  value,
  onChange,
  placeholder = '{}',
  rows = 8,
  validate,
}: JsonEditorProps) {
  const [text, setText] = useState(() => JSON.stringify(value, null, 2));
  const [error, setError] = useState<string | null>(null);

  // Sync external value changes（渲染期调整：value 变化时在提交前同步 text）
  const [prevValue, setPrevValue] = useState(value);
  if (prevValue !== value) {
    setPrevValue(value);
    try {
      const current = JSON.parse(text);
      if (JSON.stringify(current) !== JSON.stringify(value)) {
        setText(JSON.stringify(value, null, 2));
        setError(null);
      }
    } catch {
      // If current text is invalid, still update from external value
      setText(JSON.stringify(value, null, 2));
      setError(null);
    }
  }

  const handleChange = useCallback(
    (newText: string) => {
      setText(newText);
      try {
        const parsed = JSON.parse(newText);
        setError(null);
        onChange(parsed);
      } catch (e) {
        setError(e instanceof Error && e.message ? e.message : 'Invalid JSON');
      }
    },
    [onChange]
  );

  const handleFormat = () => {
    try {
      const parsed = JSON.parse(text);
      const customError = validate ? validate(parsed) : null;
      if (customError) {
        setError(customError);
        return;
      }
      setText(JSON.stringify(parsed, null, 2));
      setError(null);
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'Invalid JSON');
    }
  };

  const handleMinify = () => {
    try {
      const parsed = JSON.parse(text);
      setText(JSON.stringify(parsed));
      setError(null);
    } catch (e) {
      setError(e instanceof Error && e.message ? e.message : 'Invalid JSON');
    }
  };

  return (
    <div>
      <div className="flex gap-2 mb-2">
        <button
          type="button"
          className="secondary"
          style={{ padding: '4px 10px', fontSize: 12 }}
          onClick={handleFormat}
        >
          <AlignLeft size={12} /> 格式化
        </button>
        <button
          type="button"
          className="secondary"
          style={{ padding: '4px 10px', fontSize: 12 }}
          onClick={handleMinify}
        >
          <Minimize2 size={12} /> 压缩
        </button>
      </div>
      <textarea
        value={text}
        onChange={(e) => handleChange(e.target.value)}
        placeholder={placeholder}
        rows={rows}
        style={{
          fontFamily: 'ui-monospace, SFMono-Regular, Menlo, Monaco, Consolas, monospace',
          fontSize: 13,
          borderColor: error ? 'var(--color-error)' : undefined,
        }}
      />
      {error && (
        <div
          className="flex items-center gap-2 mt-2"
          style={{ color: 'var(--color-error)', fontSize: 12 }}
        >
          <AlertCircle size={14} />
          <span>{error}</span>
        </div>
      )}
    </div>
  );
}
